import { Box, Tooltip, Typography } from '@mui/material';
import { type MRT_Column } from 'material-react-table';
import React from 'react';
import ItemSelectDropDown from './ItemSelectDropDown';

type ItemCellProps = {
    itemName: string;
    width?: string | number;
};

/**
 * アイテム名を省略表示するセルコンポーネント
 * 長いアイテム名を持つ場合に、指定された最大幅またはテーブル幅に基づいて省略表示します。
 * 
 * @param props - コンポーネントのプロパティ
 * @returns JSX.Element - レンダリング可能なBoxコンポーネント
 */
export const ItemNameCell: React.FC<ItemCellProps> = ({
    itemName,
    width
}) => {
    return (
        <Tooltip title={itemName} arrow placement="top">
            <Box
                sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis', // ...で省略
                    whiteSpace: 'nowrap', // 改行を無効にし、一行で表示
                    width: width,
                    cursor: 'default', // ホバー時のカーソル
                }}
            >
                {itemName}
            </Box>
        </Tooltip>
    );
};

/**
 * テーブル用の汎用アイテムフィルター機能を提供します。
 * 複数選択、選択解除、省略表示に対応しています。
 * @param column - Material React Table のカラムオブジェクト
 * @param itemNames - フィルター対象のアイテム名配列（重複削除済み）
 * @param selectedItemList - 現在選択されているアイテムリスト
 * @param setSelectedItemList - アイテムリスト更新関数
 * @param width - ドロップダウンの幅
 * @returns JSX.Element - レンダリング可能なItemSelectDropDownコンポーネント
 */
export function multiSelectionFilter<R extends Record<string, any>>(
    column: MRT_Column<R>,
    itemNames: string[],
    selectedItemList: string[],
    setSelectedItemList: React.Dispatch<React.SetStateAction<string[]>>,
    width: number
) {
    return (
        <ItemSelectDropDown
            data={itemNames}
            selectedValues={selectedItemList}
            onChange={(values) => {
                setSelectedItemList(values);
                column.setFilterValue(values.length > 0 ? values : undefined);
            }}
            width={width}
            variant="standard"
            renderValue={(selected) => {
                if (selected.length === 0) return '';

                const first = selected[0];
                const others = selected.length - 1;
                const marginLeft = 0.5;
                return (
                    <Box display="flex" alignItems="center" overflow="hidden" title={selected.join(', ')}>
                        <Typography
                            noWrap
                            sx={{ flexShrink: 1, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}
                        >
                            {first}
                            {others > 0 ? ',' : ''}
                        </Typography>
                        {others > 0 && (
                            <Typography
                                sx={{
                                    flexShrink: 0, // 省略の対象にならない
                                    ml: marginLeft,
                                    color: 'text.secondary',
                                }}
                            >
                                他{others}件
                            </Typography>
                        )}
                    </Box>
                );
            }}
            MenuProps={{ disableAutoFocusItem: true }}
            column={column}
            showDeselectButton={true} // 選択解除ボタンを有効化
        />

    );
}


